import type { ComponentDefinition, System } from "../types";
import { getDefinition } from "../catalog";
import { chargeCurrentMaximum } from "../charge-current";
import { SCHEMA_VERSION } from "./version";
import { MIGRATIONS, type Migration } from "./migrations";

export type MigrateError =
  | { kind: "not-system-shape"; message: string }
  | { kind: "future-version"; fileVersion: number; supportedVersion: number };

export type MigrateResult =
  | { ok: true; system: System; appliedMigrations: number[] }
  | { ok: false; error: MigrateError };

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Shallow guard: enough to tell a system file from arbitrary JSON, not validation. */
export function isSystemShape(raw: unknown): raw is {
  nodes: unknown[];
  cables: unknown[];
  name?: unknown;
  version?: unknown;
  designProfileId?: unknown;
} {
  if (!isObject(raw)) return false;
  if (!Array.isArray(raw.nodes)) return false;
  if (!Array.isArray(raw.cables)) return false;
  if (raw.name !== undefined && typeof raw.name !== "string") return false;
  if (raw.version !== undefined && typeof raw.version !== "number") return false;
  // Preserve unknown IDs so the engine can report unsupported rules. Reject
  // malformed metadata rather than accidentally treating it as legacy marine.
  if (raw.designProfileId !== undefined && (typeof raw.designProfileId !== "string" ||
      raw.designProfileId.length === 0 || raw.designProfileId.length > 120 ||
      raw.designProfileId.trim() !== raw.designProfileId)) return false;
  return true;
}

export function runMigrations(
  raw: any,
  fromVersion: number,
  targetVersion: number,
  registry: Record<number, Migration> = MIGRATIONS,
): { result: any; applied: number[] } {
  let current = raw;
  const applied: number[] = [];
  for (let v = fromVersion; v < targetVersion; v++) {
    const fn = registry[v];
    if (!fn) {
      throw new Error(`Missing migration from v${v} to v${v + 1}`);
    }
    current = fn(current);
    applied.push(v);
  }
  return { result: current, applied };
}

/** Bad input comes back as an error result; this never throws on it. */
export function migrate(raw: unknown): MigrateResult {
  if (!isSystemShape(raw)) {
    return {
      ok: false,
      error: {
        kind: "not-system-shape",
        message: "Payload is missing required fields (nodes, cables).",
      },
    };
  }

  const fileVersion =
    typeof raw.version === "number" && raw.version >= 1 ? raw.version : 1;

  if (fileVersion > SCHEMA_VERSION) {
    return {
      ok: false,
      error: {
        kind: "future-version",
        fileVersion,
        supportedVersion: SCHEMA_VERSION,
      },
    };
  }

  const { result, applied } = runMigrations(raw, fileVersion, SCHEMA_VERSION);
  // Validate the new instance setting even for current-version imports. Keep
  // stale settings bounded if a hardware rating has since been corrected.
  const nodes = [];
  for (const node of result.nodes) {
    if (!isObject(node) || node.chargeCurrentLimitA === undefined) { nodes.push(node); continue; }
    const limit = node.chargeCurrentLimitA;
    const def = node.kind === "component" && typeof node.definitionId === "string"
      ? getDefinition(node.definitionId, node.customDefinition as ComponentDefinition | undefined) : undefined;
    const maximum = def && Array.isArray(def.terminals) && def.terminals.every(t => isObject(t) && typeof t.id === "string")
      ? chargeCurrentMaximum(def) : undefined;
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit < 0 || maximum === undefined) {
      return { ok: false, error: { kind: "not-system-shape", message: "Invalid charge current limit." } };
    }
    nodes.push(limit > maximum ? { ...node, chargeCurrentLimitA: maximum } : node);
  }
  const system: System = { ...(result as System), nodes, version: SCHEMA_VERSION };
  return { ok: true, system, appliedMigrations: applied };
}
